import { makeAutoObservable, computed } from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import {
  Element,
  ElementUpdate,
  TextElement,
  ElementStyle,
  Point,
  LineElement,
  ArrowElement,
} from '@/types';
import type { RootStore } from './RootStore';
import { HistoryStore } from './HistoryStore';
import { UIStore } from './UIStore';
import { getCombinedBounds } from '@/utils/geometry';
import { updateConnectedLines } from '@/utils/geometry/connections';

export class ProjectStore {
  rootStore!: RootStore;
  historyStore: HistoryStore;
  uiStore: UIStore;
  elements: Element[] = [];
  selectedElementIds = new Map<string, boolean>();
  clipboard: Element[] = [];

  constructor(historyStore: HistoryStore, uiStore: UIStore) {
    this.historyStore = historyStore;
    this.uiStore = uiStore;
    makeAutoObservable(this, {
      selectedElements: computed,
    });
  }

  init(rootStore: RootStore) {
    this.rootStore = rootStore;
    // Take initial snapshot so there's a baseline state to undo back to
    this.takeSnapshot();
  }

  get selectedElements(): Element[] {
    return this.elements.filter(el => this.selectedElementIds.has(el.id));
  }

  getElementById(id: string): Element | undefined {
    return this.elements.find(el => el.id === id);
  }

  select(id: string) {
    this.selectedElementIds.clear();
    this.selectedElementIds.set(id, true);
  }

  addToSelection(id: string) {
    this.selectedElementIds.set(id, true);
  }

  removeFromSelection(id: string) {
    this.selectedElementIds.delete(id);
  }

  clearSelection() {
    this.selectedElementIds.clear();
  }

  selectElements(elementIds: string[]) {
    this.selectedElementIds.clear();
    elementIds.forEach(id => this.selectedElementIds.set(id, true));
  }

  takeSnapshot() {
    const snapshot = JSON.stringify(this.elements);
    this.historyStore.addSnapshot(snapshot);
  }

  createElement(
    element: Omit<Element, 'id' | 'zIndex' | 'createdAt' | 'updatedAt'>
  ): Element {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    const newElement: Element = {
      ...element,
      id: uuidv4(),
      zIndex: this.elements.length,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    } as Element;
    this.elements.push(newElement);
    return newElement;
  }

  loadElements(elements: Element[]) {
    this.elements = elements;
    this.selectedElementIds.clear();
    // Don't take snapshot when loading - this is used for undo/redo restoration
  }

  updateElement(elementUpdate: ElementUpdate) {
    const { id, updates } = elementUpdate;
    const element = this.elements.find(el => el.id === id);
    if (element) {
      Object.assign(element, updates, { updatedAt: Date.now() });
      updateConnectedLines(element, this.elements);
    }
    // Snapshot is taken by the tool on action end
  }

  updateElements(updates: ElementUpdate[]) {
    const movedElements: Element[] = [];
    updates.forEach(update => {
      const element = this.elements.find(el => el.id === update.id);
      if (element) {
        // If this is a group being moved (but not resized), also move its child elements
        if (
          element.type === 'group' &&
          (update.updates.x !== undefined || update.updates.y !== undefined) &&
          (update.updates as any).width === undefined &&
          (update.updates as any).height === undefined
        ) {
          const deltaX = (update.updates.x || element.x) - element.x;
          const deltaY = (update.updates.y || element.y) - element.y;

          // Move all child elements
          (element as any).elementIds.forEach((childId: string) => {
            const childElement = this.elements.find(el => el.id === childId);
            if (childElement) {
              // Handle lines and arrows specially - move their points
              if (
                childElement.type === 'line' ||
                childElement.type === 'arrow'
              ) {
                const lineOrArrow = childElement as any;
                if (lineOrArrow.points && lineOrArrow.points.length > 0) {
                  lineOrArrow.points = lineOrArrow.points.map(
                    (point: Point) => ({
                      x: point.x + deltaX,
                      y: point.y + deltaY,
                    })
                  );
                }
                // Also disconnect any connections since moving breaks them
                lineOrArrow.startConnection = null;
                lineOrArrow.endConnection = null;
              } else {
                // Handle regular elements - move their x,y position
                childElement.x += deltaX;
                childElement.y += deltaY;
              }
              childElement.updatedAt = Date.now();
            }
          });
        }

        // If this is a group being resized, scale its child elements
        if (
          element.type === 'group' &&
          ((update.updates as any).width !== undefined ||
            (update.updates as any).height !== undefined)
        ) {
          const oldWidth = (element as any).width;
          const oldHeight = (element as any).height;
          const newWidth = (update.updates as any).width || oldWidth;
          const newHeight = (update.updates as any).height || oldHeight;
          const scaleX = newWidth / oldWidth;
          const scaleY = newHeight / oldHeight;
          const groupX =
            (update.updates as any).x !== undefined
              ? (update.updates as any).x
              : element.x;
          const groupY =
            (update.updates as any).y !== undefined
              ? (update.updates as any).y
              : element.y;

          // Scale and reposition all child elements
          (element as any).elementIds.forEach((childId: string) => {
            const childElement = this.elements.find(el => el.id === childId);
            if (childElement) {
              if (
                childElement.type === 'line' ||
                childElement.type === 'arrow'
              ) {
                // Handle lines and arrows - scale their points
                const lineOrArrow = childElement as any;
                if (lineOrArrow.points && lineOrArrow.points.length > 0) {
                  lineOrArrow.points = lineOrArrow.points.map(
                    (point: Point) => {
                      const relativeX = point.x - element.x;
                      const relativeY = point.y - element.y;
                      return {
                        x: groupX + relativeX * scaleX,
                        y: groupY + relativeY * scaleY,
                      };
                    }
                  );
                }
                // Disconnect connections since scaling breaks them
                lineOrArrow.startConnection = null;
                lineOrArrow.endConnection = null;
              } else {
                // Handle regular elements
                // Calculate relative position within the group
                const relativeX = childElement.x - element.x;
                const relativeY = childElement.y - element.y;

                // Scale the relative position
                const newRelativeX = relativeX * scaleX;
                const newRelativeY = relativeY * scaleY;

                // Update child element position and size
                childElement.x = groupX + newRelativeX;
                childElement.y = groupY + newRelativeY;

                // Scale dimensions if the element has them
                if ('width' in childElement && 'height' in childElement) {
                  (childElement as any).width *= scaleX;
                  (childElement as any).height *= scaleY;
                }
                if ('radiusX' in childElement && 'radiusY' in childElement) {
                  (childElement as any).radiusX *= scaleX;
                  (childElement as any).radiusY *= scaleY;
                }
              }

              childElement.updatedAt = Date.now();
            }
          });
        }

        Object.assign(element, update.updates);
        element.updatedAt = Date.now();
        movedElements.push(element);
      }
    });

    movedElements.forEach(movedEl => {
      updateConnectedLines(movedEl, this.elements);
    });
    // Snapshot is taken by the tool on action end
  }

  updateElementPoint(elementId: string, pointIndex: number, newPoint: Point) {
    const element = this.elements.find(el => el.id === elementId) as
      | (LineElement | ArrowElement)
      | undefined;
    if (element && element.points && element.points[pointIndex]) {
      element.points[pointIndex] = newPoint;
      element.updatedAt = Date.now();
      // No snapshot here for performance reasons, will be taken on drag end.
    }
  }

  addElementPoint(elementId: string, pointIndex: number, newPoint: Point) {
    const element = this.elements.find(el => el.id === elementId) as
      | (LineElement | ArrowElement)
      | undefined;
    if (element && element.points) {
      // Take snapshot BEFORE making the change
      this.takeSnapshot();
      element.points.splice(pointIndex, 0, newPoint);
      element.updatedAt = Date.now();
    }
  }

  deleteElements(ids: string[]) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    this.elements = this.elements.filter(el => !ids.includes(el.id));
    // TODO: Handle connected lines cleanup
  }

  addTextElementAndStartEditing(
    element: Omit<TextElement, 'id' | 'zIndex' | 'createdAt' | 'updatedAt'>
  ): Element {
    const newElement = this.createElement(element);
    this.uiStore.setEditingTextId(newElement.id);
    return newElement;
  }

  copySelected = () => {
    const elementsToClone: Element[] = [];
    const idMapping = new Map<string, string>(); // old id -> new id mapping

    // First, collect all elements that need to be copied (including group children)
    this.selectedElements.forEach(el => {
      if (el.type === 'group') {
        // For groups, copy the group and all its children
        const newGroupId = uuidv4();
        idMapping.set(el.id, newGroupId);

        // Copy group element
        elementsToClone.push({
          ...el,
          id: newGroupId,
        });

        // Copy all child elements
        (el as any).elementIds.forEach((childId: string) => {
          const childElement = this.elements.find(e => e.id === childId);
          if (childElement) {
            const newChildId = uuidv4();
            idMapping.set(childId, newChildId);
            elementsToClone.push({
              ...childElement,
              id: newChildId,
              groupId: newGroupId, // Update group reference
            });
          }
        });
      } else {
        // For regular elements, just copy them
        const newId = uuidv4();
        idMapping.set(el.id, newId);
        elementsToClone.push({
          ...el,
          id: newId,
        });
      }
    });

    // Update group elementIds to use new IDs
    elementsToClone.forEach(el => {
      if (el.type === 'group') {
        (el as any).elementIds = (el as any).elementIds.map(
          (oldId: string) => idMapping.get(oldId) || oldId
        );
      }
    });

    this.clipboard = elementsToClone;
  };

  paste = () => {
    if (this.clipboard.length === 0) return;

    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    // Generate new IDs for all pasted elements to avoid conflicts
    const idMapping = new Map<string, string>();
    const newElements: Element[] = [];

    // First pass: generate new IDs and create new elements
    this.clipboard.forEach(el => {
      const newId = uuidv4();
      idMapping.set(el.id, newId);

      const newElement = {
        ...el,
        id: newId,
        x: el.x + 10,
        y: el.y + 10,
        isSelected: false,
      };

      newElements.push(newElement);
    });

    // Second pass: update group references
    newElements.forEach(el => {
      if (el.type === 'group') {
        (el as any).elementIds = (el as any).elementIds.map(
          (oldId: string) => idMapping.get(oldId) || oldId
        );
      } else if (el.groupId) {
        el.groupId = idMapping.get(el.groupId) || el.groupId;
      }
    });

    // Add all new elements to the canvas
    newElements.forEach(el => this.elements.push(el));

    this.clearSelection();
    newElements.forEach(el => this.addToSelection(el.id));
  };

  deleteSelectedElements = () => {
    const idsToDelete = Array.from(this.selectedElementIds.keys());
    if (idsToDelete.length > 0) {
      // Take snapshot BEFORE making the change
      this.takeSnapshot();

      // Find all group elements that are being deleted
      const groupsToDelete = this.elements.filter(
        el => idsToDelete.includes(el.id) && el.type === 'group'
      );

      // Add child elements of deleted groups to the deletion list
      const allIdsToDelete = new Set(idsToDelete);
      groupsToDelete.forEach(group => {
        (group as any).elementIds.forEach((childId: string) => {
          allIdsToDelete.add(childId);
        });
      });

      this.elements = this.elements.filter(el => !allIdsToDelete.has(el.id));
      this.clearSelection();
    }
  };

  duplicateSelected = () => {
    if (this.selectedElements.length > 0) {
      this.copySelected();
      this.paste();
      // paste already takes a snapshot
    }
  };

  updateElementsStyle(ids: string[], style: Partial<ElementStyle>) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    ids.forEach(id => {
      const element = this.elements.find(el => el.id === id);
      if (element) {
        // Icons, SVG, and Image elements should never have fillColor, fillPattern, or strokeWidth applied
        if (
          element.type === 'icon' ||
          element.type === 'svg' ||
          element.type === 'image'
        ) {
          const {
            fillColor,
            fillPattern,
            strokeWidth,
            strokeStyle,
            ...safeStyle
          } = style;
          element.style = { ...element.style, ...safeStyle };
        } else {
          element.style = { ...element.style, ...style };
        }
      }
    });
  }

  bringToFront(ids: string[]) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    const maxZ = Math.max(...this.elements.map(el => el.zIndex));
    ids.forEach(id => {
      const element = this.elements.find(el => el.id === id);
      if (element) {
        element.zIndex = maxZ + 1;
      }
    });
  }

  sendToBack(ids: string[]) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    const minZ = Math.min(...this.elements.map(el => el.zIndex));
    ids.forEach(id => {
      const element = this.elements.find(el => el.id === id);
      if (element) {
        element.zIndex = minZ - 1;
      }
    });
  }

  groupElements(ids: string[]) {
    const elementsToGroup = this.elements.filter(el => ids.includes(el.id));
    if (elementsToGroup.length < 2) return;

    const bounds = getCombinedBounds(elementsToGroup);
    if (!bounds) return;

    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    const groupId = uuidv4();
    const groupElement: Element = {
      id: groupId,
      type: 'group',
      x: bounds.x,
      y: bounds.y,
      width: bounds.width,
      height: bounds.height,
      elementIds: ids,
      style: {
        strokeColor: '#ccc',
        strokeWidth: 1,
        strokeStyle: 'dashed',
        fillColor: 'transparent',
        fillPattern: 'solid',
        opacity: 0.5,
        fontSize: 16,
        fontFamily: 'sans-serif',
        textAlign: 'center',
        textVerticalAlign: 'middle',
        textType: 'sloppy',
      },
      isSelected: false,
      zIndex: Math.max(...elementsToGroup.map(el => el.zIndex)) + 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    elementsToGroup.forEach(el => (el.groupId = groupId));
    this.elements.push(groupElement);
  }

  ungroupElements(groupId: string) {
    const group = this.elements.find(
      el => el.id === groupId && el.type === 'group'
    );
    if (!group) return;

    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    (group as any).elementIds.forEach((elementId: string) => {
      const el = this.elements.find(e => e.id === elementId);
      if (el) {
        delete el.groupId;
      }
    });
    this.elements = this.elements.filter(el => el.id !== groupId);
  }

  alignElements(
    ids: string[],
    type: 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom'
  ) {
    const selected = this.elements.filter(el => ids.includes(el.id));
    if (selected.length < 2) return;

    const bounds = getCombinedBounds(selected);
    if (!bounds) return;

    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    selected.forEach(el => {
      const elBounds = getCombinedBounds([el]);
      if (!elBounds) return;

      let newX = el.x;
      let newY = el.y;

      switch (type) {
        case 'left':
          newX = bounds.x;
          break;
        case 'center':
          newX = bounds.x + bounds.width / 2 - elBounds.width / 2;
          break;
        case 'right':
          newX = bounds.x + bounds.width - elBounds.width;
          break;
        case 'top':
          newY = bounds.y;
          break;
        case 'middle':
          newY = bounds.y + bounds.height / 2 - elBounds.height / 2;
          break;
        case 'bottom':
          newY = bounds.y + bounds.height - elBounds.height;
          break;
      }
      this.updateElement({ id: el.id, updates: { x: newX, y: newY } });
    });
  }

  bringForward(ids: string[]) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    const sortedElements = [...this.elements].sort(
      (a, b) => a.zIndex - b.zIndex
    );
    ids.forEach(id => {
      const currentIndex = sortedElements.findIndex(el => el.id === id);
      if (currentIndex < sortedElements.length - 1) {
        const currentElement = sortedElements[currentIndex];
        const nextElement = sortedElements[currentIndex + 1];
        if (currentElement && nextElement) {
          [currentElement.zIndex, nextElement.zIndex] = [
            nextElement.zIndex,
            currentElement.zIndex,
          ];
        }
      }
    });
    this.elements = this.elements.map(
      el => sortedElements.find(s => s.id === el.id) || el
    );
  }

  sendBackward(ids: string[]) {
    // Take snapshot BEFORE making the change
    this.takeSnapshot();
    const sortedElements = [...this.elements].sort(
      (a, b) => a.zIndex - b.zIndex
    );
    ids.forEach(id => {
      const currentIndex = sortedElements.findIndex(el => el.id === id);
      if (currentIndex > 0) {
        const currentElement = sortedElements[currentIndex];
        const prevElement = sortedElements[currentIndex - 1];
        if (currentElement && prevElement) {
          [currentElement.zIndex, prevElement.zIndex] = [
            prevElement.zIndex,
            currentElement.zIndex,
          ];
        }
      }
    });
    this.elements = this.elements.map(
      el => sortedElements.find(s => s.id === el.id) || el
    );
  }

  distributeElements({
    ids,
    type,
  }: {
    ids: string[];
    type: 'horizontal' | 'vertical';
  }) {
    const selected = this.elements
      .filter(el => ids.includes(el.id))
      .sort((a, b) => (type === 'horizontal' ? a.x - b.x : a.y - b.y));
    if (selected.length < 3) return;

    const first = selected[0];
    const last = selected[selected.length - 1];

    if (!first || !last) return;

    // Take snapshot BEFORE making the change
    this.takeSnapshot();

    const totalSpan =
      type === 'horizontal'
        ? last.x + (last as any).width - first.x
        : last.y + (last as any).height - first.y;

    const totalSize = selected.reduce(
      (sum, el) =>
        sum + (type === 'horizontal' ? (el as any).width : (el as any).height),
      0
    );
    const spacing = (totalSpan - totalSize) / (selected.length - 1);

    let currentPos =
      type === 'horizontal'
        ? first.x + (first as any).width
        : first.y + (first as any).height;

    for (let i = 1; i < selected.length - 1; i++) {
      const el = selected[i];
      if (!el) continue;
      currentPos += spacing;
      const updates =
        type === 'horizontal' ? { x: currentPos } : { y: currentPos };
      this.updateElement({ id: el.id, updates });
      currentPos +=
        type === 'horizontal' ? (el as any).width : (el as any).height;
    }
  }

  disconnectElement(elementId: string) {
    const element = this.elements.find(el => el.id === elementId);
    if (element && (element.type === 'line' || element.type === 'arrow')) {
      // Take snapshot BEFORE making the change
      this.takeSnapshot();
      this.updateElement({
        id: elementId,
        updates: { startConnection: null, endConnection: null } as any,
      });
    }
  }
}
